import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { usersApi } from "../../services/api";
import { Badge, Table, Pagination } from "../../components/ui";
import { showToast } from "../../utils/toast";
import {
  User,
  Wallet,
  Globe2,
  Heart,
  Headphones,
  ChevronLeft,
  ChevronDown,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Lock,
  Sun,
  Moon,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Smartphone,
} from "lucide-react";

function Spinner() {
  return (
    <div style={{ padding: 60, textAlign: "center" }}>
      <div style={{ width: 32, height: 32, margin: "0 auto 10px", border: "3px solid var(--border)", borderTop: "3px solid #0F3D3E", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
      <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Loading…</div>
    </div>
  );
}

const TABS = [
  { key: "profile",       label: "Profile",         Icon: User },
  { key: "wallet",        label: "Wallet",           Icon: Wallet },
  { key: "kundli",        label: "Kundli",           Icon: Globe2 },
  { key: "compatibility", label: "Compatibility",    Icon: Heart },
  { key: "support",       label: "Support Queries",  Icon: Headphones },
];

function TabBar({ active, onChange, counts }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 24 }}>
      {TABS.map(t => {
        const isActive = active === t.key;
        const count = counts[t.key];
        const { Icon } = t;
        return (
          <button key={t.key} onClick={() => onChange(t.key)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 16px", borderRadius: 24,
              border: isActive ? "none" : "1px solid var(--border)",
              background: isActive ? "#0F3D3E" : "transparent",
              color: isActive ? "#fff" : "var(--text-secondary)",
              fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 500,
              cursor: "pointer", transition: "all 0.15s",
              boxShadow: isActive ? "0 2px 12px rgba(15,61,62,0.35)" : "none",
            }}>
            <Icon size={14} strokeWidth={2} />
            {t.label}
            {count != null && count > 0 && (
              <span style={{
                background: isActive ? "rgba(255,255,255,0.25)" : "rgba(15,61,62,0.1)",
                color: isActive ? "#fff" : "#0F3D3E",
                fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 10, marginLeft: 2,
              }}>{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", boxShadow: "var(--shadow-sm)" }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "rgba(15,61,62,0.03)", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 4, height: 18, borderRadius: 2, background: "#0F3D3E", flexShrink: 0 }} />
        <h3 style={{ fontFamily: "var(--font-display)", fontSize: 14, color: "#0F3D3E", fontWeight: 600 }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Empty({ msg = "No data." }) {
  return <div style={{ padding: "36px 24px", color: "var(--text-muted)", fontSize: 13, textAlign: "center" }}>{msg}</div>;
}

function Bar({ value, color = "#0F3D3E" }) {
  return (
    <div style={{ height: 5, background: "var(--border)", borderRadius: 3, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${Math.min(value ?? 0, 100)}%`, background: color, borderRadius: 3, transition: "width 0.4s ease" }} />
    </div>
  );
}

function DoshaPill({ label }) {
  return (
    <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "rgba(239,68,68,0.08)", color: "#dc2626", border: "1px solid rgba(239,68,68,0.22)" }}>
      {label}
    </span>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
      <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", flexShrink: 0, marginRight: 16 }}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--text-primary)", textAlign: "right", wordBreak: "break-all" }}>{value || "—"}</span>
    </div>
  );
}

function KChip({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, background: "rgba(15,61,62,0.04)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 10px" }}>
      <span style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--text-primary)", fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function resolveNames(c) {
  return [c.personAName || "Person A", c.personBName || "Person B"];
}

function PersonChip({ name, bg, textColor }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <div style={{ width: 30, height: 30, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: textColor, flexShrink: 0 }}>
        {name[0]?.toUpperCase()}
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{name}</span>
    </div>
  );
}

const KOOT_ROWS = [
  ["varna",        "Varna",        "Karmic Classification"],
  ["vashya",       "Vashya",       "Evolutionary Character"],
  ["tara",         "Tara",         "Luck"],
  ["yoni",         "Yoni",         "Sexual Compatibility"],
  ["graha_maitri", "Graha Maitri", "Love & Affection"],
  ["gana",         "Gana",         "Temperament"],
  ["bhakoota",     "Bhakoot",      "Emotional Bonding"],
  ["nadi",         "Nadi",         "Health & Progeny"],
];

function Toggle({ value, onChange }) {
  return (
    <div onClick={() => onChange(!value)}
      style={{ width: 44, height: 24, borderRadius: 12, background: value ? "#0F3D3E" : "#e2e8f0", border: `1px solid ${value ? "#0a2d2e" : "#cbd5e1"}`, position: "relative", cursor: "pointer", flexShrink: 0, transition: "background 0.25s" }}>
      <div style={{ position: "absolute", top: 3, left: value ? 22 : 3, width: 16, height: 16, borderRadius: "50%", background: value ? "#fff" : "#94a3b8", transition: "left 0.25s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
    </div>
  );
}

const STATUS_OPTIONS = ["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"];
const STATUS_META = {
  OPEN:        { bg: "rgba(59,130,246,0.08)",  color: "#2563eb", border: "rgba(59,130,246,0.22)",  Icon: AlertTriangle },
  IN_PROGRESS: { bg: "rgba(245,158,11,0.08)",  color: "#d97706", border: "rgba(245,158,11,0.22)",  Icon: Clock },
  RESOLVED:    { bg: "rgba(34,197,94,0.08)",   color: "#16a34a", border: "rgba(34,197,94,0.22)",   Icon: CheckCircle2 },
  CLOSED:      { bg: "rgba(107,114,128,0.08)", color: "#6b7280", border: "rgba(107,114,128,0.22)", Icon: Lock },
};

function SupportQueriesTab({ supportQueries }) {
  const [updatingId,   setUpdatingId]   = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [localQueries, setLocalQueries] = useState(supportQueries);

  useEffect(() => { setLocalQueries(supportQueries); }, [supportQueries]);

  const handleStatusChange = async (query, newStatus) => {
    if (newStatus === query.status) { setOpenDropdown(null); return; }
    setUpdatingId(query.id);
    setOpenDropdown(null);
    try {
      await usersApi.updateSupportQueryStatus(query.id, newStatus);
      setLocalQueries(prev => prev.map(q => q.id === query.id ? { ...q, status: newStatus } : q));
      showToast("Status updated successfully");
    } catch {
      showToast("Failed to update status", "error");
    }
    setUpdatingId(null);
  };

  return (
    <Section title={`Support Queries (${localQueries.length})`}>
      {localQueries.length === 0 ? (
        <Empty msg="No support queries raised by this user." />
      ) : (
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          {localQueries.map((q, i) => {
            const meta       = STATUS_META[q.status] || STATUS_META["CLOSED"];
            const { Icon: StatusIcon } = meta;
            const isUpdating = updatingId === q.id;
            const isOpen     = openDropdown === q.id;

            return (
              <div key={q.id || i} style={{ background: "rgba(15,61,62,0.02)", border: "1px solid var(--border)", borderRadius: 12, overflow: "visible", position: "relative" }}>
                {/* Header */}
                <div style={{
                  padding: "12px 18px",
                  background: "linear-gradient(135deg,rgba(15,61,62,0.05),rgba(208,179,106,0.04))",
                  display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8,
                  borderBottom: "1px solid var(--border)", borderRadius: "12px 12px 0 0",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#0F3D3E", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                    <div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", letterSpacing: "0.3px" }}>Query ID</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#0F3D3E", background: "rgba(15,61,62,0.08)", border: "1px solid rgba(15,61,62,0.15)", borderRadius: 20, padding: "1px 8px", display: "inline-block", letterSpacing: "0.3px", marginTop: 2 }}>
                        {q.id?.slice(-10) || "—"}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ position: "relative" }}>
                      <button
                        disabled={isUpdating}
                        onClick={() => setOpenDropdown(isOpen ? null : q.id)}
                        title="Change status"
                        style={{
                          fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20,
                          background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
                          display: "inline-flex", alignItems: "center", gap: 5,
                          cursor: isUpdating ? "not-allowed" : "pointer", opacity: isUpdating ? 0.6 : 1,
                          transition: "all 0.15s", outline: "none", fontFamily: "var(--font-body)",
                        }}
                      >
                        {isUpdating ? (
                          <>
                            <Loader2 size={10} strokeWidth={2} style={{ animation: "spin 0.6s linear infinite" }} />
                            Updating…
                          </>
                        ) : (
                          <>
                            <StatusIcon size={11} strokeWidth={2} />
                            {q.status}
                            <ChevronDown size={10} strokeWidth={2.5} style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }} />
                          </>
                        )}
                      </button>

                      {isOpen && (
                        <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 999, background: "#0F3D3E", borderRadius: 10, minWidth: 160, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden" }}>
                          {STATUS_OPTIONS.map(s => {
                            const m = STATUS_META[s];
                            const { Icon: OptionIcon } = m;
                            const isCurrent = q.status === s;
                            return (
                              <div key={s}
                                onClick={() => handleStatusChange(q, s)}
                                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
                                onMouseLeave={e => e.currentTarget.style.background = isCurrent ? "rgba(255,255,255,0.08)" : "transparent"}
                                style={{ padding: "9px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, background: isCurrent ? "rgba(255,255,255,0.08)" : "transparent", transition: "background 0.12s" }}
                              >
                                <OptionIcon size={13} strokeWidth={2} color={isCurrent ? "#fff" : "rgba(255,255,255,0.6)"} />
                                <span style={{ fontSize: 12, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? "#fff" : "rgba(255,255,255,0.75)" }}>
                                  {s.replace("_", " ")}
                                </span>
                                {isCurrent && (
                                  <svg style={{ marginLeft: "auto" }} xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                        {q.createdAt ? new Date(q.createdAt).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}
                      </div>
                      {q.updatedAt && q.updatedAt !== q.createdAt && (
                        <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                          Updated: {new Date(q.updatedAt).toLocaleDateString("en-IN")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: "14px 18px" }}>
                  {q.description ? (
                    <p style={{ fontSize: 13, color: "var(--text-primary)", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {q.description}
                    </p>
                  ) : (
                    <span style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>No description provided.</span>
                  )}
                  {q.isActive != null && (
                    <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>Active:</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: q.isActive ? "#16a34a" : "#6b7280" }}>{q.isActive ? "Yes" : "No"}</span>
                    </div>
                  )}
                  {q.screenshotUrl && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6 }}>
                        Screenshot
                      </div>
                      <div style={{ border: "1px solid var(--border)", borderRadius: "var(--radius)", background: "var(--bg-input)", minHeight: 240, display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>
                        <div onClick={() => window.open(q.screenshotUrl, "_blank", "noopener,noreferrer")} style={{ display: "inline-block", cursor: "pointer", lineHeight: 0 }}>
                          <img
                            src={q.screenshotUrl}
                            alt="Support screenshot"
                            style={{ maxWidth: "100%", maxHeight: 216, objectFit: "contain", display: "block" }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              e.currentTarget.parentElement.parentElement.innerHTML =
                                `<div style="padding:16px;text-align:center;font-size:12px;color:var(--text-muted)">Screenshot unavailable</div>`;
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Click on image to open full size</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function UserWalletPage() {
  const { userId } = useParams();
  const { state }  = useLocation();
  const navigate   = useNavigate();

  const [user,           setUser]           = useState(null);
  const [wallet,         setWallet]         = useState(null);
  const [txns,           setTxns]           = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [kundli,         setKundli]         = useState([]);
  const [compatibility,  setCompatibility]  = useState([]);
  const [txPage,         setTxPage]         = useState(0);
  const [loading,        setLoading]        = useState(true);
  const [activeTab,      setActiveTab]      = useState("profile");
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [supportQueries, setSupportQueries] = useState([]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [userRes, walletRes, kundliRes, compatRes, supportRes] = await Promise.all([
          usersApi.getById(userId),
          usersApi.getWallet(userId).catch(() => ({ data: { data: null } })),
          usersApi.getKundli(userId).catch(() => ({ data: { data: [] } })),
          usersApi.getCompatibility(userId).catch(() => ({ data: { data: [] } })),
          usersApi.getSupportQueries(userId).catch(() => ({ data: { data: [] } })),
        ]);

        // getById returns a trimmed DTO — it does NOT include deviceType / deviceStatus.
        // state.user (passed from the users list via navigate) has the full object.
        // We merge: state.user first (full list fields), then API response on top
        // (fresher data like active/updatedAt), so we get the best of both.
        const apiUser   = userRes?.data?.data?.content?.[0] || userRes?.data?.data || {};
        const stateUser = state?.user || {};
        const mergedUser = { ...stateUser, ...apiUser };

        setUser(mergedUser);
        setWallet(walletRes.data.data);
        setKundli(kundliRes.data.data || []);
        setCompatibility(compatRes.data.data || []);
        setSupportQueries(supportRes.data.data || []);
      } catch {}
      setLoading(false);
    };
    init();
  }, [userId]);

  useEffect(() => {
    usersApi.getTransactions(userId, txPage)
      .then(r => setTxns(r.data.data || { content: [], totalPages: 0, totalElements: 0 }))
      .catch(() => {});
  }, [userId, txPage]);

  const handleToggleStatus = async () => {
    if (!user || togglingStatus) return;
    setTogglingStatus(true);
    try {
      await usersApi.toggleStatus(user.id, !(user?.active ?? user?.isActive));
      const refreshed = await usersApi.getById(userId);
      const apiUser   = refreshed?.data?.data?.content?.[0] || refreshed?.data?.data || {};
      // Re-merge after toggle so deviceType/deviceStatus are still preserved
      setUser(prev => ({ ...prev, ...apiUser }));
      showToast("Status updated");
    } catch {
      showToast("Failed", "error");
    }
    setTogglingStatus(false);
  };

  const txColumns = [
    { key: "createdAt", label: "Date", render: v => v ? new Date(v).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—" },
    {
      key: "transactionType", label: "Type", render: v => (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {v === "CREDIT"
            ? <TrendingUp size={13} strokeWidth={2} color="#16a34a" />
            : <TrendingDown size={13} strokeWidth={2} color="#dc2626" />
          }
          <span style={{ color: v === "CREDIT" ? "#16a34a" : "#dc2626", fontWeight: 600, fontSize: 12 }}>{v}</span>
        </div>
      )
    },
    { key: "credits", label: "Credits", render: (v, row) => <span style={{ fontWeight: 700, color: row.transactionType === "CREDIT" ? "#16a34a" : "#dc2626" }}>{row.transactionType === "CREDIT" ? "+" : ""}{v}</span> },
    { key: "balanceAfter", label: "Balance After", render: v => <span style={{ fontWeight: 600, color: "#0F3D3E" }}>{v}</span> },
    { key: "source", label: "Source", render: v => <Badge variant="default">{v || "—"}</Badge> },
    { key: "description", label: "Description", render: v => <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{v}</span> },
  ];

  const tabCounts = {
    profile:       0,
    wallet:        txns.totalElements || 0,
    kundli:        kundli.length,
    compatibility: compatibility.length,
    support:       supportQueries.length,
  };

  if (loading) return (
    <div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <button onClick={() => navigate("/users")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 14, marginBottom: 16 }}>
        <ChevronLeft size={18} strokeWidth={2} /> Back
      </button>
      <Spinner />
    </div>
  );

  return (
    <div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* User header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <button onClick={() => navigate("/users")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
          <ChevronLeft size={22} strokeWidth={2} />
        </button>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#0F3D3E", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
          {(user?.name || "?")[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 20, color: "var(--text-primary)", marginBottom: 2 }}>{user?.name || "Unknown"}</h1>
          <div style={{ fontSize: 12, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {user?.mobile}{user?.email ? ` · ${user.email}` : ""}
          </div>
        </div>
        <Badge variant={(user?.active ?? user?.isActive) ? "success" : "danger"}>
          {(user?.active ?? user?.isActive) ? "Active" : "Inactive"}
        </Badge>
      </div>

      <TabBar active={activeTab} onChange={setActiveTab} counts={tabCounts} />

      {/* ── PROFILE ── */}
      {activeTab === "profile" && (
        <Section title="User Profile">
          <div style={{ padding: "4px 20px 16px" }}>
            <Row label="User ID"       value={user?.userId} />
            <Row label="Name"          value={user?.name} />
            <Row label="Mobile"        value={user?.mobile} />
            <Row label="Email"         value={user?.email} />
            <Row label="Gender"        value={user?.gender} />
            <Row label="Date of Birth" value={user?.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString("en-IN") : null} />
            <Row label="Birth Time"    value={user?.birthTime} />
            <Row label="Birth Place"   value={user?.birthPlace} />
            <Row label="Latitude"      value={user?.birthPlaceLatitude || null} />
            <Row label="Longitude"     value={user?.birthPlaceLongitude || null} />
            <Row label="Source"        value={user?.source} />
            <Row label="Reg. Status"   value={user?.registrationStatus} />
            <Row label="Device Type"   value={user?.deviceType} />
            <Row label="Device Status" value={user?.deviceStatus} />
            <Row label="Reg. Date"     value={user?.createdAt ? new Date(user.createdAt).toLocaleString("en-IN") : null} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0 4px", marginTop: 4, borderTop: "2px solid var(--border)" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>Account Status</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{(user?.active ?? user?.isActive) ? "Currently active" : "Currently inactive"}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: (user?.active ?? user?.isActive) ? "#0F3D3E" : "var(--text-muted)" }}>
                  {(user?.active ?? user?.isActive) ? "Active" : "Inactive"}
                </span>
                <Toggle value={!!(user?.active ?? user?.isActive)} onChange={handleToggleStatus} />
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* ── WALLET + TRANSACTIONS ── */}
      {activeTab === "wallet" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {wallet ? (
            <div style={{ background: "linear-gradient(135deg,#0F3D3E,#1a5c5e)", borderRadius: 16, padding: "28px 32px", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20 }}>
              <div>
                <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 8 }}>Current Balance</div>
                <div style={{ fontSize: 48, fontWeight: 700, fontFamily: "var(--font-display)", lineHeight: 1 }}>{wallet.balance?.toLocaleString("en-IN")}</div>
                <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>credits</div>
              </div>
              <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
                {[["Wallet ID", wallet.id?.slice(-10)], ["Status", wallet.active ? "Active" : "Inactive"], ["Created", wallet.createdAt ? new Date(wallet.createdAt).toLocaleDateString("en-IN") : "—"]].map(([k, v]) => (
                  <div key={k}><div style={{ fontSize: 11, opacity: 0.65, marginBottom: 5 }}>{k}</div><div style={{ fontSize: 14, fontWeight: 600 }}>{v}</div></div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ background: "rgba(15,61,62,0.05)", border: "1px solid var(--border)", borderRadius: 12, padding: 32, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>No wallet found.</div>
          )}
          <Section title={`Transactions (${txns.totalElements})`}>
            {txns.content.length === 0 ? <Empty msg="No transactions yet." /> : (
              <>
                <Table columns={txColumns} data={txns.content} emptyText="No transactions" />
                <div style={{ padding: "0 16px" }}><Pagination page={txPage} totalPages={txns.totalPages} onChange={setTxPage} /></div>
              </>
            )}
          </Section>
        </div>
      )}

      {/* ── KUNDLI BASIC ── */}
      {activeTab === "kundli" && (
        <Section title={`Kundli Charts (${kundli.length})`}>
          {kundli.length === 0 ? <Empty msg="No kundli charts generated yet." /> : (
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
              {kundli.map((k, i) => {
                const d = k.basicDetails?.data || {};
                return (
                  <div key={i} style={{ background: "rgba(15,61,62,0.03)", borderRadius: 12, border: "1px solid var(--border)", overflow: "hidden" }}>
                    <div style={{ padding: "14px 18px", background: "linear-gradient(135deg,rgba(15,61,62,0.08),rgba(208,179,106,0.06))", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)", marginBottom: 4 }}>{d.full_name || "Unknown"}</div>
                        <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                          {d.gender && <Badge variant={d.gender === "male" ? "info" : "default"}>{d.gender}</Badge>}
                          {d.sunsign && (
                            <Badge variant="warning">
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                <Sun size={11} strokeWidth={2} /> {d.sunsign}
                              </span>
                            </Badge>
                          )}
                          {d.moonsign && (
                            <Badge variant="info">
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                <Moon size={11} strokeWidth={2} /> {d.moonsign}
                              </span>
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 3 }}>
                          {d.year && d.month && d.day ? `${d.day}/${d.month}/${d.year}` : ""}
                          {d.hour && d.minute ? ` · ${d.hour}:${d.minute.padStart(2, "0")}` : ""}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{d.place}</div>
                        <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 3 }}>
                          {k.createdAt ? new Date(k.createdAt).toLocaleDateString("en-IN") : ""}
                        </div>
                      </div>
                    </div>
                    <div style={{ padding: "12px 16px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 8, marginBottom: 12 }}>
                        <KChip label="Nakshatra"   value={d.nakshatra} />
                        <KChip label="Tithi"       value={d.tithi} />
                        <KChip label="Paksha"      value={d.paksha} />
                        <KChip label="Vaar"        value={d.vaar} />
                        <KChip label="Yoga"        value={d.yoga} />
                        <KChip label="Karana"      value={d.karana} />
                        <KChip label="Gana"        value={d.gana} />
                        <KChip label="Nadi"        value={d.nadi} />
                        <KChip label="Varna"       value={d.varna} />
                        <KChip label="Vashya"      value={d.vashya} />
                        <KChip label="Yoni"        value={d.yoni} />
                        <KChip label="Tatva"       value={d.tatva} />
                        <KChip label="Chandramasa" value={d.chandramasa} />
                        <KChip label="Yunja"       value={d.yunja} />
                        <KChip label="Timezone"    value={d.timezone ? `UTC+${d.timezone}` : null} />
                        <KChip label="Ayanamsha"   value={d.ayanamsha} />
                      </div>
                      {d.paya && (
                        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Paya:</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#d97706" }}>{d.paya.type} — {d.paya.result}</span>
                        </div>
                      )}
                      {d.rashi_akshar && (
                        <div style={{ fontSize: 12, color: "var(--text-muted)", background: "rgba(208,179,106,0.08)", borderRadius: 6, padding: "6px 10px" }}>
                          <span style={{ fontWeight: 600, color: "#a88a45" }}>Rashi Akshar: </span>{d.rashi_akshar}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      )}

      {/* ── COMPATIBILITY ── */}
      {activeTab === "compatibility" && (
        <Section title={`Compatibility Checks (${compatibility.length})`}>
          {compatibility.length === 0 ? <Empty msg="No compatibility checks." /> : (
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
              {compatibility.map((c, i) => {
                const milan    = c.rawResponse?.ashtakoot_milan || {};
                const manglik  = c.rawResponse?.manglik_dosha;
                const nadiD    = c.rawResponse?.nadi_dosha;
                const bhakootD = c.rawResponse?.bhakoot_dosha;
                const scoreCol = c.ashtakootScore >= 25 ? "#16a34a" : c.ashtakootScore >= 18 ? "#d97706" : "#dc2626";
                const [nA, nB] = resolveNames(c);
                return (
                  <div key={i} style={{ background: "var(--bg-card)", borderRadius: 12, border: "1px solid rgba(208,179,106,0.25)", overflow: "hidden" }}>
                    <div style={{ padding: "16px 20px", background: "linear-gradient(135deg,rgba(15,61,62,0.05),rgba(208,179,106,0.07))", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                          <PersonChip name={nA} bg="rgba(59,130,246,0.14)" textColor="#1d4ed8" />
                          <span style={{ fontSize: 15, color: "var(--text-muted)" }}>×</span>
                          <PersonChip name={nB} bg="rgba(236,72,153,0.12)" textColor="#be185d" />
                        </div>
                        {c.compatibilityLevel && <Badge variant={c.finalScore >= 70 ? "success" : c.finalScore >= 50 ? "warning" : "danger"}>{c.compatibilityLevel}</Badge>}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 38, fontWeight: 700, color: scoreCol, fontFamily: "var(--font-display)", lineHeight: 1 }}>
                          {c.ashtakootScore}<span style={{ fontSize: 15, color: "var(--text-muted)", fontWeight: 400 }}>/36</span>
                        </div>
                        {c.finalScore != null && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>Final: {c.finalScore}%</div>}
                        <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                          {c.createdAt ? new Date(c.createdAt).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : ""}
                        </div>
                      </div>
                    </div>
                    <div style={{ padding: "14px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
                      {c.ashtakootResult && <p style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.6, margin: 0 }}>{c.ashtakootResult}</p>}
                      {Object.keys(milan).length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Ashtakoot Breakdown</div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: 6 }}>
                            {KOOT_ROWS.map(([key, label, area]) => {
                              const entry = milan[key] || {}, got = entry.points_obtained, max = entry.max_ponits;
                              const pct = max ? (got / max) * 100 : 0;
                              const col = got != null && max != null ? got >= max * 0.6 ? "#16a34a" : got === 0 ? "#dc2626" : "#d97706" : "var(--text-muted)";
                              return (
                                <div key={key} style={{ background: "rgba(15,61,62,0.03)", borderRadius: 8, padding: "8px 10px", border: "1px solid var(--border)" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                                    <div><div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div><div style={{ fontSize: 10, color: "var(--text-muted)" }}>{area}</div></div>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: col }}>{got != null ? `${got}/${max}` : "—"}</span>
                                  </div>
                                  <Bar value={pct} color={col} />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                        {[
                          ["Love", c.lovePercentage, "#e11d48", Heart],
                          ["Understanding", c.understandingPercentage, "#0F3D3E", null],
                          ["Dominance", c.dominancePercentage, "#d97706", null],
                        ].map(([lbl, val, col, Icon]) => val != null && (
                          <div key={lbl} style={{ flex: 1, minWidth: 130 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                {Icon && <Icon size={11} strokeWidth={2} color={col} />}
                                {lbl}
                              </span>
                              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{val}%</span>
                            </div>
                            <Bar value={val} color={col} />
                          </div>
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-muted)", letterSpacing: "0.05em" }}>DOSHA</span>
                        {manglik?.p1 === "true" && <DoshaPill label={`Manglik — ${nA}`} />}
                        {manglik?.p2 === "true" && <DoshaPill label={`Manglik — ${nB}`} />}
                        {nadiD === "true"        && <DoshaPill label="Nadi Dosha" />}
                        {bhakootD === "true"     && <DoshaPill label="Bhakoot Dosha" />}
                        {manglik?.p1 !== "true" && manglik?.p2 !== "true" && nadiD !== "true" && bhakootD !== "true" && (
                          <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "rgba(34,197,94,0.1)", color: "#16a34a", border: "1px solid rgba(34,197,94,0.25)" }}>No Dosha</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      )}

      {/* ── SUPPORT QUERIES ── */}
      {activeTab === "support" && (
        <SupportQueriesTab supportQueries={supportQueries} />
      )}
    </div>
  );
}